import HBIcon from "@/components/HBIcon";
import Image from "@/components/Image";
import { TrackerA } from "@/components/Tracker";
import imageUrl from "@/utils/imageUrl";
import { useDebounceFn, useHover } from "ahooks";
import { Carousel } from "antd";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./card.module.less";
import Video from "./video";
interface Iprops {
  topicId: string;
  pinCount: number;
  pins: any;
  filter: any;
  isHomeImage?: boolean;
  homeImage?: string;
  title: string;
  style?: any;
  className?: string;
}
enum pinType {
  overlong = 1, // 超长竖图
  long, // 竖图
  square, // 方图
  short, // 横图
}
const pinTypeInfo = {
  [pinType.overlong]: {
    width: 134,
  },
  [pinType.long]: {
    width: 134,
  },

  [pinType.square]: {
    width: 180,
  },
  [pinType.short]: {
    width: 200,
  },
};
export default function Card({
  topicId,
  pinCount,
  pins,
  filter,
  isHomeImage = false,
  homeImage,
  title,
  className,
  style,
}: Iprops) {
  const ref = useRef(null);
  const carouselRef = useRef(null);
  const isHovering = useHover(ref);
  const [carouse, setCarouse] = useState({
    from: undefined,
    to: undefined,
  });
  const { run: hoverChange } = useDebounceFn(
    () => {
      if (isHovering) {
        carouselRef?.current?.next();
      }
    },
    {
      wait: 400,
    }
  );
  useEffect(() => {
    hoverChange();
  }, [isHovering, hoverChange]);

  // 根据图片宽高选择不同类型
  const chooseImgType = (width, height) => {
    let proportion = width / height;
    if (proportion < 0.56) {
      return pinType.overlong;
    } else if (proportion < 0.8) {
      return pinType.long;
    } else if (proportion > 1) {
      return pinType.short;
    } else {
      return pinType.square;
    }
  };
  const videoInfo = useMemo(() => {
    if (
      pins[0]?.file_material?.material_category === "视频模板" ||
      pins[0]?.file_material?.material_category === "视频"
    ) {
      let imgType = chooseImgType(
        pins?.[0]?.file?.width,
        pins?.[0]?.file?.height
      );
      let pinInfo = pinTypeInfo[imgType];
      return {
        ...pinInfo,
        file: pins[0].file,
        src: pins[0].file_material?.extra?.video_mask,
      };
    }
    return false;
  }, [pins]);
  const onImgLoad = (evt) => {
    const target = evt.target;
    target.classList.add("transparent-img-bg");
  };
  const beforeChange = (from, to) => {
    setCarouse({ from, to });
  };
  const getImgClassName = (coverImg) => {
    /**
     * 1.图片宽>高，高度固定
     * 2.图片宽<=高 宽度固定
     */
    if (!coverImg) {
      return undefined;
    }

    if (coverImg?.width > coverImg?.height) {
      return styles["heightFixed"];
    } else {
      return styles["widthFixed"];
    }
  };

  return (
    // eslint-disable-next-line react/jsx-no-target-blank
    <TrackerA
      className={classNames(
        styles.card,
        {
          [styles.homeImage]: isHomeImage,
        },
        className
      )}
      style={style}
      ref={ref}
      href={`/topics/${topicId}-${filter?.value}`}
      target="_blank"
      data-gd-expose="topic_expose"
      data-gd-click="topic_click"
      data-topic-id={filter?.value}
      data-topic-type="二级主题"
      data-topic-title={filter.title}
      data-content-source={`内容合集_${title}_${filter.title}`}
    >
      {isHomeImage ? (
        <div className={styles.bigImageContent}>
          {videoInfo ? (
            <Video
              src={videoInfo.src}
              style={{ width: 504, height: 280 }}
              isHovering={isHovering}
              coverFile={videoInfo.file}
            ></Video>
          ) : (
            <Image
              src={homeImage || imageUrl(pins?.[0]?.file, { width: 504 })}
              className={classNames(
                styles.bigImage,
                getImgClassName(homeImage ? undefined : pins?.[0]?.file)
              )}
              alt={homeImage ? "" : pins?.[0].raw_text}
              title={homeImage ? "" : pins?.[0].raw_text}
            ></Image>
          )}
        </div>
      ) : (
        <div className={styles.carouselContent}>
          {videoInfo ? (
            <div className={styles.wrapper}>
              <div className={styles.content}>
                <Video
                  src={videoInfo.src}
                  style={{ width: videoInfo?.width }}
                  isHovering={isHovering}
                  coverFile={videoInfo.file}
                ></Video>
              </div>
            </div>
          ) : (
            <Carousel
              autoplay={isHovering}
              dots={false}
              ref={carouselRef}
              pauseOnHover={false}
              autoplaySpeed={1500}
              beforeChange={beforeChange}
              lazyLoad="ondemand"
            >
              {pins?.map((item, index) => {
                let imgType = chooseImgType(item.file.width, item.file.height);
                let pinInfo = pinTypeInfo[imgType];
                return (
                  <div className={styles.wrapper} key={index}>
                    <div
                      className={classNames(styles.content, {
                        [styles.overlong]: imgType === pinType.overlong,
                        [styles.from]: index === carouse.from,
                      })}
                    >
                      <Image
                        className={styles.img}
                        src={imageUrl(item.file, { width: 240 })}
                        srcSet={imageUrl(item.file, { width: 480 }) + " 2x"}
                        alt={item.raw_text}
                        title={item.raw_text}
                        width={pinInfo?.width}
                        onLoad={onImgLoad}
                        /**
                         * 不能全部都设置为懒加载，会和轮播组件切换时候的加载机制冲突，
                         * 轮播组件会在切换前再加载，使用lazy之后会出现切换后再加载，导致闪烁
                         * 所以只需设置首张懒加载，剩下的由组件控制即可
                         */
                        loading={index === 0 ? "lazy" : "eager"}
                      ></Image>
                    </div>
                  </div>
                );
              })}
            </Carousel>
          )}
        </div>
      )}

      <div className={styles.footer}>
        <div className={styles.left} title={filter.title}>
          {filter.title}
        </div>
        <div className={styles.right}>
          {pinCount >= 10000 ? "9999+" : pinCount}张
          <HBIcon type="ic_chevron_right" className={styles.icon} />
        </div>
      </div>
    </TrackerA>
  );
}
